import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";

const Field = ({ label, children }) => <label className="text-xs block"><span className="ax-label">{label}</span><div className="mt-1">{children}</div></label>;
const cls = "w-full bg-muted/60 border h-9 px-2 text-sm outline-none focus:border-primary";
const BRANCHES = [{ v: "DEL", t: "Delhi (FBD)" }, { v: "MUM", t: "Mumbai" }, { v: "UNASSIGNED", t: "Unassigned" }];

export const withCurrent = (list, cur) => (cur && !list.some((x) => (x?.v ?? x) === cur) ? [...list, { v: cur, t: `${cur} (former)` }] : list);

export const Sel = ({ value, onChange, opts, testid, blank = "—" }) => (
  <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
    <option value="">{blank}</option>
    {opts.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.v} value={o.v}>{o.t}</option>))}
  </select>
);

const Text = ({ k, f, set, testid, ...rest }) => <input data-testid={testid} className={cls} value={f[k] || ""} onChange={set(k)} {...rest} />;

const useLeadSetup = (open, user) => {
  const [cfg, setCfg] = useState(null);
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ status: "A" });
  const [acc, setAcc] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/sales/config").then((r) => setCfg(r.data));
    api.get("/staff").then((r) => setStaff(r.data));
    setF({ status: "A", owner: user?.staff_name || "", branch: (user?.branches || []).find((b) => b !== "*") || "MUM" });
    setAcc(null);
  }, [open, user]);
  return { cfg, staff, f, setF, acc, setAcc };
};

const leadBody = (f) => {
  const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== undefined));
  if (body.estimated_value) body.estimated_value = Number(body.estimated_value);
  return body;
};

const ContactFields = ({ f, set, cfg, check }) => {
  const { field } = useAuth();
  return (<>
    <Field label="Company name"><Text k="company_name" f={f} set={set} testid="lead-form-company" onBlur={check} /></Field>
    <Field label="Lead / contact name"><Text k="contact_name" f={f} set={set} testid="lead-form-contact" /></Field>
    {field("lead.phone") === "edit" && <Field label="Phone"><Text k="phone" f={f} set={set} testid="lead-form-phone" /></Field>}
    {field("lead.email") === "edit" && <Field label="Email"><Text k="email" f={f} set={set} testid="lead-form-email" onBlur={check} /></Field>}
    <Field label="Type"><Sel testid="lead-form-type" value={f.type} onChange={set("type")} opts={cfg.types} /></Field>
    <Field label="Source of lead"><Sel testid="lead-form-source" value={f.source} onChange={set("source")} opts={cfg.sources} /></Field>
    <Field label="Referred by"><Text k="referred_by" f={f} set={set} testid="lead-form-referred-by" placeholder="For referral leads" /></Field>
    <Field label="Campaign"><Text k="campaign" f={f} set={set} testid="lead-form-campaign" /></Field>
    <Field label="Product category"><Sel testid="lead-form-category" value={f.product_category} onChange={set("product_category")} opts={cfg.product_categories} /></Field>
    <Field label="City"><Text k="city" f={f} set={set} testid="lead-form-city" /></Field>
    <Field label="Branch"><Sel testid="lead-form-branch" value={f.branch} onChange={set("branch")} opts={BRANCHES} /></Field>
  </>);
};

const StatusFields = ({ f, set, cfg, staff }) => {
  const { can, field, user } = useAuth();
  return (<>
    <Field label="Lead warmth"><Sel testid="lead-form-warmth" value={f.warmth} onChange={set("warmth")} opts={cfg.warmth} /></Field>
    <Field label="Type of customer"><Sel testid="lead-form-customer-type" value={f.customer_type} onChange={set("customer_type")} opts={cfg.customer_types} /></Field>
    <Field label="Status"><Sel testid="lead-form-status" value={f.status} onChange={set("status")} opts={cfg.statuses.map((s) => ({ v: s.code, t: s.label }))} /></Field>
    <Field label="Owner"><Sel testid="lead-form-owner" value={f.owner} onChange={set("owner")} opts={can("sales", "assign") ? staff : [user?.staff_name].filter(Boolean)} /></Field>
    <Field label="Next contact date"><Text k="next_contact_date" f={f} set={set} testid="lead-form-next-date" type="date" /></Field>
    {field("lead.estimated_value") === "edit" && <Field label="Est. value (₹ Lakhs, pre-GST)"><Text k="estimated_value" f={f} set={set} testid="lead-form-value" type="number" /></Field>}
    {f.status === "G" && <Field label="Reason for lead lost"><Text k="lost_reason" f={f} set={set} testid="lead-form-lost-reason" /></Field>}
    <div className="sm:col-span-2"><Field label="Requirement / comments"><textarea data-testid="lead-form-requirement" className={`${cls} h-20 py-2`} value={f.requirement || ""} onChange={set("requirement")} /></Field></div>
  </>);
};

const AccountWarning = ({ acc }) => (acc && (acc.visible.length > 0 || acc.hidden_matches) ? (
  <div data-testid="account-duplicate-warning" className="border border-amber-500/40 text-amber-300 text-xs p-3">
    Possible existing account: {acc.visible.map((a) => `${a.name} (${a.account_id}, ${a.owner || "no owner"})`).join("; ")}
    {acc.hidden_matches && " A similar account also exists in a portfolio you can't access. Check with your manager before you continue."}
  </div>
) : null);

export const LeadForm = ({ open, onClose, onCreated }) => {
  const { user } = useAuth();
  const { cfg, staff, f, setF, acc, setAcc } = useLeadSetup(open, user);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const check = () => {
    if (!f.company_name && !f.email) return;
    api.get("/accounts/check", { params: { name: f.company_name, email: f.email } }).then((r) => setAcc(r.data)).catch(() => {});
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads", leadBody(f));
      if (data.duplicates?.length) toast.warning(`Created ${data.lead.lead_id}. It looks like ${data.duplicates.length} existing lead(s), so it's been sent to Duplicate Review.`);
      else toast.success(`Lead ${data.lead.lead_id} created`);
      onCreated?.(data.lead);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card" data-testid="lead-form-dialog">
        <DialogHeader><DialogTitle className="font-display">New lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ContactFields f={f} set={set} cfg={cfg} check={check} />
          <StatusFields f={f} set={set} cfg={cfg} staff={staff} />
        </div>
        <AccountWarning acc={acc} />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="border h-9 px-4 text-sm" data-testid="lead-form-cancel">Cancel</button>
          <button disabled={busy} onClick={submit} className="bg-primary text-primary-foreground h-9 px-4 text-sm disabled:opacity-50" data-testid="lead-form-submit">Create lead</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
